import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { newsLetterSubscriberService } from '@/services/altingross';

// ─── FilterOperator enum ───────────────────────────────────────────────────
export const FilterOperator = {
  Equals:      'Equals',
  NotEquals:   'NotEquals',
  GreaterThan: 'GreaterThan',
  LessThan:    'LessThan',
  Contains:    'Contains',
};

// ─── Thunks ────────────────────────────────────────────────────────────────
export const fetchSubscriber = createAsyncThunk(
  'newsLetterSubscriber/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const res = await newsLetterSubscriberService.getById(id);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);

export const fetchSubscribers = createAsyncThunk(
  'newsLetterSubscriber/fetchList',
  async ({ page = 0, size = 20, filter = null } = {}, { rejectWithValue }) => {
    try {
      const res = await newsLetterSubscriberService.getList(page, size, filter);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);

export const exportSubscribers = createAsyncThunk(
  'newsLetterSubscriber/export',
  async ({ fileName = 'subscribers', sheetName = 'Sheet1' } = {}, { rejectWithValue }) => {
    try {
      const res = await newsLetterSubscriberService.exportExcel(fileName, sheetName);
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);

export const deleteSubscriber = createAsyncThunk(
  'newsLetterSubscriber/delete',
  async (id, { rejectWithValue }) => {
    try {
      await newsLetterSubscriberService.deleteById(id);
      return id;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);


const initialState = {
  list: {
    data:    null,   // { page, size, count, hasNext, hasPrev, totalPages, items[] }
    loading: false,
    error:   null,
  },
  detail: {
    data:    null,
    loading: false,
    error:   null,
  },
  export: {
    loading: false,
    error:   null,
  },
};

// ─── Slice ─────────────────────────────────────────────────────────────────
const newsLetterSubscriberSlice = createSlice({
  name: 'newsLetterSubscriber',
  initialState,
  reducers: {
    resetDetail: (state) => {
      state.detail = { data: null, loading: false, error: null };
    },
    resetList: (state) => {
      state.list = { data: null, loading: false, error: null };
    },
  },
  extraReducers: (builder) => {
    // fetchSubscriber
    builder
      .addCase(fetchSubscriber.pending, (state) => {
        state.detail.loading = true;
        state.detail.error   = null;
      })
      .addCase(fetchSubscriber.fulfilled, (state, action) => {
        state.detail.loading = false;
        state.detail.data    = action.payload;
      })
      .addCase(fetchSubscriber.rejected, (state, action) => {
        state.detail.loading = false;
        state.detail.error   = action.payload;
      });

    // fetchSubscribers
    builder
      .addCase(fetchSubscribers.pending, (state) => {
        state.list.loading = true;
        state.list.error   = null;
      })
      .addCase(fetchSubscribers.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data    = action.payload;
      })
      .addCase(fetchSubscribers.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error   = action.payload;
      });

    // exportSubscribers
    builder
      .addCase(exportSubscribers.pending, (state) => {
        state.export.loading = true;
        state.export.error   = null;
      })
      .addCase(exportSubscribers.fulfilled, (state) => {
        state.export.loading = false;
      })
      .addCase(exportSubscribers.rejected, (state, action) => {
        state.export.loading = false;
        state.export.error   = action.payload;
      });
    // deleteSubscriber
    builder
      .addCase(deleteSubscriber.fulfilled, (state, action) => {
        if (state.list.data?.items) {
          state.list.data.items  = state.list.data.items.filter((i) => i.id !== action.payload);
          state.list.data.count  = Math.max(0, (state.list.data.count ?? 1) - 1);
        }
      });
  },
});

export const { resetDetail, resetList } = newsLetterSubscriberSlice.actions;
export default newsLetterSubscriberSlice.reducer;
